import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useLocation } from "react-router"
import { consumeReturnUrl } from "../utils/returnUrl"

/**
 * Shared hook that restores a saved return URL after login, or falls back
 * to a default route. Used by both admin_app and school_web InitialView
 * components to redirect from "/" after Cognito authentication.
 *
 * @param {() => string} getDefaultRoute - Function returning the default route path
 */
export const useReturnUrlRedirect = getDefaultRoute => {
  const location = useLocation()
  const { pathname } = location
  const navigate = useNavigate()
  const hasRedirectedRef = useRef(false)

  useEffect(() => {
    if (pathname !== "/" || hasRedirectedRef.current) {
      return
    }

    hasRedirectedRef.current = true

    const returnUrl = consumeReturnUrl()
    if (returnUrl) {
      // If the saved URL has a root pathname (e.g., "/?settings=integrations"),
      // redirect to getDefaultRoute() with the saved query/hash instead of "/"
      // which would leave the app on the blank InitialView route
      const savedPathname = returnUrl.split("?")[0].split("#")[0]

      if (savedPathname === "/") {
        const extra = returnUrl.substring(1) // everything after "/"
        navigate(`${getDefaultRoute()}${extra}`, { replace: true })
      } else {
        navigate(returnUrl, { replace: true })
      }
      return
    }

    // Preserve query params from root URL (e.g., /?settings=integrations
    // from OAuth callback → /school?settings=integrations)
    const route = getDefaultRoute()
    const target = location.search ? `${route}${location.search}` : route
    navigate(target, { replace: true })
  }, [pathname, navigate, getDefaultRoute, location.search])
}
